import React, { useState } from 'react';
import {
    ComposedChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    Brush
} from 'recharts';

const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
        return (
            <div className="bg-slate-800 text-white p-3 rounded-lg shadow-xl border border-slate-700 max-w-xs z-50">
                <p className="font-bold text-amber-500 mb-2 border-b border-slate-600 pb-1">{label}</p>
                <div className="space-y-1">
                    {payload.map((entry, index) => (
                        <div key={index} className="flex items-center justify-between text-sm">
                            <span style={{ color: entry.color }}>{entry.name}:</span>
                            <span className="font-mono font-bold ml-3">{Number(entry.value).toFixed(4)}</span>
                        </div>
                    ))}
                </div>
            </div>
        );
    }
    return null;
};

const ChartRepetitibilidadPro = ({ data }) => {
    const [showInfo, setShowInfo] = useState(false);

    const chartData = Object.entries(data || {}).map(([date, seriesList]) => {
        const point = { name: date };
        seriesList.forEach(s => {
            if (s.serie === "KPI L1") point.KPI_L1 = s.valor;
            if (s.serie === "LS1") point.LS1 = s.valor;
            if (s.serie === "LI1") point.LI1 = s.valor;
        });
        return point;
    });

    chartData.sort((a, b) => {
        const [da, ma, ya] = a.name.split('/').map(Number);
        const [db, mb, yb] = b.name.split('/').map(Number);
        return new Date(ya, ma - 1, da) - new Date(yb, mb - 1, db);
    });

    return (
        <div className="bg-white p-6 rounded-2xl shadow-lg border border-slate-100 flex flex-col h-[450px] relative">
            <div className="mb-6 flex justify-between items-start">
                <div className="flex items-start gap-2">
                    <div>
                        <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                            📊 KPI de Repetibilidad (L1)
                        </h3>
                        <p className="text-sm text-slate-500 mt-1">
                            Control de variación en mediciones consecutivas.
                            <span className="text-blue-500 text-xs ml-2">(Use el control inferior para hacer Zoom)</span>
                        </p>
                    </div>
                    <button
                        onClick={() => setShowInfo(!showInfo)}
                        className="mt-1 text-slate-400 hover:text-blue-600 transition-colors"
                        title="Ver fórmula de cálculo"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                    </button>

                    {showInfo && (
                        <div className="absolute top-14 left-4 z-50 bg-slate-800 text-white p-4 rounded-xl shadow-2xl border border-slate-700 w-72 animated fadeIn">
                            <div className="flex justify-between items-start mb-2">
                                <h4 className="font-bold text-amber-500 text-sm">Fórmula de Cálculo</h4>
                                <button onClick={() => setShowInfo(false)} className="text-slate-400 hover:text-white"><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg></button>
                            </div>
                            <p className="text-xs text-slate-300 leading-relaxed">
                                El KPI L1 evalúa la consistencia midiendo el <strong>Rango (Máx - Mín)</strong> de los errores de volumen en 3 cargas consecutivas. Un valor bajo indica alta repetibilidad.
                            </p>
                            <div className="mt-3 text-xs bg-slate-900 p-2 rounded border border-slate-700 font-mono text-center">
                                KPI = Max(Errors) - Min(Errors)
                            </div>
                        </div>
                    )}
                </div>
                <div className="px-3 py-1 bg-blue-50 text-blue-700 text-xs font-bold rounded-full uppercase tracking-wider">
                    Análisis de Estabilidad
                </div>
            </div>

            <div className="flex-grow overflow-x-auto">
                <div className="min-w-[700px] h-full">
                    <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart
                            data={chartData}
                            margin={{ top: 10, right: 30, left: 0, bottom: 20 }}
                        >
                            <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" vertical={false} />
                            <XAxis
                                dataKey="name"
                                scale="point"
                                padding={{ left: 20, right: 20 }}
                                tick={{ fontSize: 11, fill: "#64748b" }}
                                axisLine={{ stroke: '#e2e8f0' }}
                                tickLine={false}
                                dy={10}
                            />
                            <YAxis
                                tick={{ fontSize: 11, fill: "#64748b" }}
                                domain={['auto', 'auto']}
                                tickFormatter={(value) => value.toFixed(4)}
                                axisLine={false}
                                tickLine={false}
                            />
                            <Tooltip content={<CustomTooltip />} />
                            <Legend
                                verticalAlign="top"
                                align="right"
                                wrapperStyle={{ paddingBottom: '20px', fontSize: '11px' }}
                                iconType="circle"
                            />

                            {/* LS1 - Red Dashed for Limit */}
                            <Line
                                type="step"
                                dataKey="LS1"
                                stroke="#ef4444"
                                strokeDasharray="4 4"
                                dot={false}
                                strokeWidth={2}
                                name="Límite Superior"
                            />

                            {/* KPI L1 - Professional Blue */}
                            <Line
                                type="monotone"
                                dataKey="KPI_L1"
                                stroke="#0f172a"
                                strokeWidth={3}
                                dot={{ r: 4, fill: "#0f172a", strokeWidth: 0 }}
                                activeDot={{ r: 8, fill: "#f59e0b", stroke: "#fff", strokeWidth: 2 }}
                                name="KPI Calculado"
                                isAnimationActive={true}
                                animationDuration={1500}
                            />

                            {/* LI1 - Red Dashed for Limit */}
                            <Line
                                type="step"
                                dataKey="LI1"
                                stroke="#ef4444"
                                strokeDasharray="4 4"
                                dot={false}
                                strokeWidth={2}
                                name="Límite Inferior"
                            />

                            <Brush
                                dataKey="name"
                                height={30}
                                stroke="#64748b"
                                travellerWidth={10}
                                fill="#f8fafc"
                            />
                        </ComposedChart>
                    </ResponsiveContainer>
                </div>
            </div>
        </div>
    );
};

export default ChartRepetitibilidadPro;
